import {WebSocketContext} from '~/webSocket/WebSocketProvider';
import {useCallback, useContext} from "react";
import {useStoreState} from "~/store";
import {CancelledServiceWebSocketData, ClosedIssueWebSocketData, DeletedServiceWebSocketData, DepartmentChangeWebSocketData, Message} from '~/types';
import store from "~/store";
import useTranslation from "~/app/hooks/useTranslation";
import { ChatWebSocketActions, MessageType } from '../data/statuses/chat';

export type ChatMessageContext = {
    machineId?: string | number
    projectId?: string | number
    roleId?: string | number
    attachmentUrls?: string[]
};

const useWebSocket = () => {
    const {reconnect, send, close} = useContext(WebSocketContext);
    const user = useStoreState(state => state.user.data);
    const {t} = useTranslation('issue');
    const newIssueMsg = t('success.issue');

    const prepareMessage = useCallback((
        messageText: string,
        action: string = '',
        issueId: (number | string) = '',
        type: MessageType = MessageType.ISSUE,
        context: ChatMessageContext = {},
    ): Message => {
        return {
            message: messageText,
            issueId: `${issueId}`,
            user: user.id,
            senderId: user.id,
            senderName: user.name,
            type: type,
            ...(context.machineId && { machineId: String(context.machineId) }),
            ...(context.projectId && { projectId: String(context.projectId) }),
            ...(context.roleId && { roleId: String(context.roleId) }),
            ...(context.attachmentUrls && context.attachmentUrls.length > 0 && { attachmentUrls: context.attachmentUrls }),
            ...(action === 'add' && {actions: ChatWebSocketActions.ADDUSER}),
            ...(action === 'remove' && {actions: ChatWebSocketActions.DELETEUSER}),
            ...(action === 'registeredIssue' && {actions: ChatWebSocketActions.REGISTERISSUE, actionName: 'registeredIssue'}),
            ...(action === 'registeredTraining' && {actions: ChatWebSocketActions.REGISTERTRAINING, actionName: 'registeredTraining'})
        }
    }, [user.id, user.name]);

    const sendMessage = useCallback((
        messageText: string,
        action: string = '',
        issueId: string = '',
        type: MessageType = MessageType.ISSUE,
        context: ChatMessageContext = {},
    ) => {
        send && send(JSON.stringify(prepareMessage(messageText, action, issueId, type, context)));
    }, [send, prepareMessage]);

    const updateUnreadCount = useCallback(() => {
        if (user.id) {
            send && send(JSON.stringify({ updateUnreadCount: true, user: user.id }));
        }
    }, [send, user.id]);

    const syncIssues = useCallback((issueIds: (number | string)[]) => {
        send && send(JSON.stringify({isNewIssue: 'newIssue'}));
        issueIds && issueIds.forEach((issueId) => {
            send && send(JSON.stringify({
                "addedDataForIssueBoard": {
                    'type': 'addedIssue',
                    'switchBetweenIssueAndWorkorder': 0,
                    'issueId': issueId
                }
            }));
            send && send(JSON.stringify(prepareMessage(newIssueMsg, 'registeredIssue', issueId)));
        });
    }, [send, newIssueMsg, prepareMessage]);

    const syncUpdatedIssueDetail = useCallback((issueId: (number | string)) => {
        issueId && send && send(
            JSON.stringify({
                "addedDataForIssueBoard": {
                    'type': 'updatedIssue',
                    'issueId': issueId,
                    'isFromDueDateFilter' : false,
                    'hasDueDateChanged' : false,
                    'searchForIssueCard' : false,
                    'dueDate': null,
                    'switchBetweenIssueAndWorkorder': 0,
                }
            })
        );
    }, [send]);

    const syncDepartmentChange = useCallback((data: DepartmentChangeWebSocketData) => {
        data.issueId && send && send(
            JSON.stringify({
                "addedDataForIssueBoard": {
                    'type': 'changedDepartment',
                    'issueIds': [data.issueId],
                    'oldDepartment' : String(data.oldDepartmentId),
                    'department' : String(data.newDepartmentId)
                }
            })
        )
    }, [send])

    const syncClosedIssue = useCallback((data: ClosedIssueWebSocketData) => {
        data.issueId && send && send(
            JSON.stringify({
                "addedDataForIssueBoard": {
                    'type': 'deletedIssue',
                    'issueId': data.issueId,
                    'department': data.departmentId,
                    'switchBetweenIssueAndWorkorder': 1
                }
            })
        )
    }, [send])

    const syncCancelledService = useCallback((data: CancelledServiceWebSocketData) => {
        data.serviceId && send && send(
            JSON.stringify({
                "addedDataForIssueBoard": {
                    'type': 'cancelService',
                    'excavatorId': data.machineId,
                    'department_id': data.departmentId,
                    'service': data.serviceId,
                    'switchBetweenIssueAndWorkorder': 1
                  }
            })
        )
    }, [send])

    const syncDeletedService = useCallback((data: DeletedServiceWebSocketData) => {
        data.serviceId && send && send(
            JSON.stringify({
                "addedDataForIssueBoard": {
                    'type': 'deletedService',
                    'serviceId': data.serviceId,
                    'department': data.departmentId,
                    'switchBetweenIssueAndWorkorder': 1
                }
            })
        )
    }, [send])

    const connectUser = useCallback(() => {
        const userId = store.getState().user.data.id;
        if (userId) {
            const message = {
                'user': userId,
                'connectionType': 'open'
            };
            send && send(JSON.stringify(message));
        }
    }, [send]);

    return {
        sendMessage,
        updateUnreadCount,
        close,
        syncIssues,
        syncUpdatedIssueDetail,
        syncDepartmentChange,
        syncClosedIssue,
        syncCancelledService,
        syncDeletedService,
        connectUser,
        reconnect
    }
};

export default useWebSocket;
